import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
    OkResponse,
    PartnerMeResponse,
    PartnerNotificationsResponse,
    PartnerOnboardingView,
    ReadAllNotificationsResponse,
    ShareChannelKey,
    UpdateOnboardingBody,
} from "@mfa-aff/shared";
import { mockAdminView } from "../mocks/admin-view";
import { listNotificationsMock, markAllNotificationsReadMock, markNotificationReadMock, meMock } from "../mocks/me";
import { get, qs, send } from "./client";
import { rememberImpersonation } from "./impersonation-state";
import { partnerPaths } from "./paths";

export const meKey = ["partner", "me"] as const;

/** Mock `GET /me`, as an admin's read-only view while `?mock_impersonation=1` is on (mocks/admin-view.ts). */
function meFromMock(): PartnerMeResponse {
    const me = structuredClone(meMock);
    const view = mockAdminView(me.affiliate.id);
    return view ? { ...me, user: null, session: { method: "impersonation" }, impersonation: view } : me;
}

/**
 * `GET /me`: who is signed in and what the panel should show. `impersonation` set = an admin's
 * read-only view; remembered for the tab so its end goes back to the admin panel (impersonation-state.ts).
 */
export const useMe = () =>
    useQuery({
        queryKey: meKey,
        queryFn: async () => {
            const me = await get<PartnerMeResponse>(partnerPaths.me, meFromMock);
            rememberImpersonation(me.impersonation?.adminUrl ?? null);
            return me;
        },
    });

/**
 * `PUT /onboarding {dismissed}`. The steps themselves are computed server-side from data (national
 * code, Sheba, first code, first share); the checklist can only hide (or show again).
 */
export const useUpdateOnboarding = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: UpdateOnboardingBody) =>
            send<PartnerOnboardingView>("PUT", partnerPaths.onboarding, body, () => {
                meMock.onboarding.dismissed = body.dismissed;
                return structuredClone(meMock.onboarding);
            }),
        onSuccess: () => qc.invalidateQueries({ queryKey: meKey }),
    });
};

/**
 * `POST /onboarding/share {channel}`: the partner copied a code or link (`copy`) or shared it to a
 * messenger. Ticks getting-started steps 1 and 2 on the server. Sent once per channel kind per page
 * load (the server only stamps the first time anyway); failures are silent.
 */
const reported = new Set<string>();
export const useRecordShare = () => {
    const qc = useQueryClient();
    return (channel: ShareChannelKey) => {
        // An admin's read-only view never stamps the partner's getting-started steps.
        if (qc.getQueryData<PartnerMeResponse>(meKey)?.impersonation) return;
        const kind = channel === "copy" ? "copy" : "share";
        if (reported.has(kind)) return;
        reported.add(kind);
        void send<PartnerOnboardingView>("POST", partnerPaths.onboardingShare, { channel }, () => {
            meMock.onboarding.refLinkDone = true;
            if (channel !== "copy") meMock.onboarding.firstShareDone = true;
            return structuredClone(meMock.onboarding);
        })
            .then((onboarding) => qc.setQueryData<PartnerMeResponse>(meKey, (me) => (me ? { ...me, onboarding } : me)))
            .catch(() => reported.delete(kind));
    };
};

// ──────────────────────────────────────────────────────── notifications ──

export const notificationsKey = ["partner", "notifications"] as const;

/** Page size of the inbox (`?limit`). */
export const NOTIFICATIONS_PAGE = 20;

/**
 * `GET /notifications?before&limit`, newest first. A page shorter than the limit is the last one;
 * otherwise the next page is `before = <last id>`. `unread` is the same total on every page.
 */
export const useNotifications = () =>
    useInfiniteQuery({
        queryKey: notificationsKey,
        initialPageParam: undefined as string | undefined,
        queryFn: ({ pageParam }) =>
            get<PartnerNotificationsResponse>(`${partnerPaths.notifications}${qs({ before: pageParam, limit: NOTIFICATIONS_PAGE })}`, () =>
                listNotificationsMock(pageParam, NOTIFICATIONS_PAGE),
            ),
        getNextPageParam: (last) => (last.items.length < NOTIFICATIONS_PAGE ? undefined : last.items[last.items.length - 1]?.id),
    });

/** `POST /notifications/:id/read`. */
export const useMarkNotificationRead = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => send<OkResponse>("POST", partnerPaths.notificationRead(id), undefined, () => markNotificationReadMock(id)),
        onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKey }),
    });
};

/** `POST /notifications/read-all`. */
export const useMarkAllNotificationsRead = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => send<ReadAllNotificationsResponse>("POST", partnerPaths.notificationsReadAll, undefined, () => markAllNotificationsReadMock()),
        onSuccess: () => qc.invalidateQueries({ queryKey: notificationsKey }),
    });
};
